import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ChangeDetectorRef,
	OnDestroy,
	SimpleChanges,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import {
	ContactsTable,
	EMAIL_VALIDATED_TOOLTIP,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
	TruckSearchTable,
} from 'cargo-chief-commons';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CarrierDataFeedbackDialog } from '../../project/capacity-search-panel/carrier-data-feedback/carrier-data-feedback.component';
import { AddContactFormDialog } from '../add-contact/add-contact.component';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { environment } from '@environment/environment';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { COMPARE } from '@domain/constants/functions';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { CarrierFeedbackService } from '@service/carrierFeedback/carrier-feedback.service';
import { SearchContactsDialogComponent } from '../search-contacts-modal/search-contacts-modal.component';
import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { BaseComponent } from '@shared/base/base.component';
import { SharedService } from '@service/shared/shared.service';
import { LogCallService } from '@service/log-call/log-call.service';

import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-carrier-contacts',
	templateUrl: './carrier-contacts.component.html',
	styleUrls: ['./carrier-contacts.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierContactsComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() contactList;
	@Input() carrierId;
	@Input() oldCarrierId;
	@Input() carrier;
	@Input() isCarrierProfile: boolean = false;
	@Input() editPrimaryContact: boolean = false;
	@Input() incorrectContactInfoEnabled: boolean = false;
	@Input() lane: TruckSearchTable;
	@Input() isEditContact: boolean = false;
	@Input() isCarrierUpdate: boolean = false;
	@Input() isCarrierLoggedIn: boolean = false;
	@Input() searchTruckForm = null;
	@Input() showContactSearchIcon = false;
	@Input() mapContactToProjectColumnEnabled: boolean = false;
	@Input() projectCarrierMap!: any;
	@Input() rowSelectionEnabled: boolean = false;
	@Input() showAddContactIcon: boolean = true;
	@Input() hideSetEmailRecipientColumn: boolean = false;
	@Input() hidereportContactColumn: boolean = false;
	@Input() PAGE_SIZE: number = 10;
	@Input() tableBorderColor: string = '#ccc';

	// CN-8616: optional load + bid context. Only the V2 quote-details bid list passes
	// these; when present, clicking a contact's phone/email also opens the shared
	// "Log a call" modal and (on save) marks the bid Contacted. Other callers (carrier
	// profile, projects, offers) leave these unset, so phone/email stay plain
	// tel:/mailto: links.
	@Input() logCallLoadId: string;
	@Input() logCallBidId: string;
	@Input() showBidContactsInfo: boolean = false;

	@Output() updateContactsList: EventEmitter<any> = new EventEmitter();
	@Output() onProjectCarrierMapContact: EventEmitter<any> = new EventEmitter();
	// Emits the bid id when a call logged from this modal marks the bid Contacted.
	@Output() contactLogged: EventEmitter<string> = new EventEmitter();

	hasPrioritizedContact: boolean = false;
	isContactMappedToProject: boolean = false;

	totalNumberOfContacts = 0;

	contactsDataTable = new MatTableDataSource<ContactsTable>([]);
	displayedColumns = [];
	carrierProfileColumns = [];
	selectedContactRow!: ContactsTable;
	showProgressBar = false;

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	EMAIL_VALIDATED_TOOLTIP = EMAIL_VALIDATED_TOOLTIP;

	bidOfferContactId: string = null;
	bidCcContactIds: string[] = [];

	constructor(
		private urlService: UrlService,
		private restService: RestService,
		public utilService: UtilService,
		public dialog: MatDialog,
		private segmentWrapper: SegmentWrapperService,
		public cargochief: Cargochief,
		private carrierFeedbackService: CarrierFeedbackService,
		private cdRef: ChangeDetectorRef,
		private sharedService: SharedService,
		private logCallService: LogCallService
	) {
		super();
		this.sharedService
			.getAddNewProjectCarrierMapContactEvent()
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((data: any) => {
				if (IS_NOT_NULL_OR_UNDEFINED(this.carrierId) && data?.carrierId === this.carrierId) {
					this.openContactDialog(null);
				}
			});
	}

	ngOnInit() {
		this.displayedColumns = [
			'firstName',
			'lastName',
			'email',
			'emailValidation',
			'emailFailedReason',
			'phoneNumber',
			'primaryContact',
			'emailLastValidated',
			'actions',
		];
		this.carrierProfileColumns = [
			'firstName',
			'lastName',
			'email',
			'emailValidation',
			'phoneNumber',
			'mobileNumber',
		];

		if (this.isCarrierProfile) {
			this.displayedColumns = [...this.carrierProfileColumns];
			if (this.editPrimaryContact) {
				this.displayedColumns.push('primaryContact');
			}
		}
		if (this.incorrectContactInfoEnabled) {
			this.displayedColumns.push('contactFeedback');
		}
		if (this.isEditContact) {
			this.displayedColumns.push('editColumn');
		}
		if (!this.hideSetEmailRecipientColumn) {
			this.displayedColumns.push('markAsAccountContact');
		}
		if (!this.hidereportContactColumn) {
			this.displayedColumns.push('reportColumn');
		}
		if (this.mapContactToProjectColumnEnabled) {
			this.displayedColumns.push('offerContactColumn');
			this.displayedColumns.push('ccContactColumn');
		}
		if (this.showBidContactsInfo) {
			this.displayedColumns.push('bidContactInfo');
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['logCallBidId'] || changes['showBidContactsInfo']) {
			this.loadBidContactRoles();
		}
		this.buildDatatableView();
	}

	buildDatatableView() {
		this.hasPrioritizedContact = false;
		this.isContactMappedToProject = false;
		if (!this.utilService.isNullOrUndefined(this.contactList) && this.contactList.length > 0) {
			this.applyBidContactRoles();
			const mappedContactMapIds = this.getMappedContactMapIds();
			const offerContactMapId = this.getOfferContactMapId();

			this.contactList.forEach((contact) => {
				const isMapped = mappedContactMapIds.includes(contact.id);
				contact.contactMappedToProject = isMapped;
				contact.isOfferContact = isMapped && contact.id === offerContactMapId;
				if (isMapped) {
					this.isContactMappedToProject = true;
				} else if (contact.prioritizedContact) {
					this.hasPrioritizedContact = true;
				}
			});

			this.contactList = this.contactList.filter((contact: ContactsTable) => {
				if (
					IS_NOT_EMPTY(contact.firstName) ||
					IS_NOT_EMPTY(contact.lastName) ||
					IS_NOT_EMPTY(contact.emailAddress) ||
					IS_NOT_EMPTY(contact.phone) ||
					IS_NOT_EMPTY(contact.mobile)
				) {
					return contact;
				}
			});
			this.sortContactList();
			this.contactsDataTable = new MatTableDataSource(this.contactList);
		} else {
			this.contactsDataTable = new MatTableDataSource([]);
		}
		this.totalNumberOfContacts = this.contactsDataTable.data.length;
		setTimeout(() => {
			this.initializeSortAndPage();
		});
	}

	initializeSortAndPage() {
		this.contactsDataTable.paginator = this.paginator;
		this.contactsDataTable.sort = this.sort;
		this.cdRef.detectChanges();
	}

	// carrierContactMapIds mapped to this project (TO + CCs)
	getMappedContactMapIds(): string[] {
		if (this.projectCarrierMap?.contactDTOs?.length) {
			return this.projectCarrierMap.contactDTOs.map((contact) => contact.id);
		}
		if (this.projectCarrierMap?.contactDTO?.id) {
			return [this.projectCarrierMap.contactDTO.id];
		}
		return [];
	}

	// carrierContactMapId of the offer (TO) contact; falls back to the primary-first first contact
	getOfferContactMapId(): string {
		const pcm = this.projectCarrierMap;
		if (!pcm) {
			return null;
		}
		if (pcm.primaryCarrierContactMapId) {
			return pcm.primaryCarrierContactMapId;
		}
		if (pcm.contactDTOs?.length) {
			return pcm.contactDTOs[0].id;
		}
		return pcm.contactDTO?.id || null;
	}

	// Order: offer (TO) first, CCs next, then the rest
	sortContactList() {
		let offerContacts = [],
			ccContacts = [],
			otherContacts = [];
		this.contactList.forEach((contact) => {
			if (this.isContactMappedToProject) {
				if (contact.isOfferContact) {
					offerContacts.push(contact);
				} else if (contact.contactMappedToProject) {
					ccContacts.push(contact);
				} else {
					otherContacts.push(contact);
				}
			} else if (
				(!this.hasPrioritizedContact && contact.primaryContact) ||
				contact.prioritizedContact
			) {
				offerContacts.push(contact);
			} else {
				otherContacts.push(contact);
			}
		});
		const byName = (a, b) => {
			if (IS_NOT_NULL_OR_UNDEFINED(a.firstName) && IS_NOT_NULL_OR_UNDEFINED(b.firstName)) {
				if (a.firstName.toLowerCase() === b.firstName.toLowerCase()) {
					if (IS_NOT_NULL_OR_UNDEFINED(a.lastName) && IS_NOT_NULL_OR_UNDEFINED(b.lastName)) {
						return COMPARE(a.lastName.toLowerCase(), b.lastName.toLowerCase(), true);
					}
					return -1;
				}
				return COMPARE(a.firstName.toLowerCase(), b.firstName.toLowerCase(), true);
			}
			return -1;
		};
		this.contactList = offerContacts.concat(ccContacts.sort(byName), otherContacts.sort(byName));
	}

	async primaryContactToggled(event, toggledContact) {
		let isPrimaryContact = event.checked;
		const carrierContactMapId = toggledContact.id;
		let allowPrimaryContact = true;
		// If the contact is not authorized and we are trying to make it primary.
		// Then open a dialog to validate the change.
		if (!toggledContact.vettingAuthorized && isPrimaryContact) {
			allowPrimaryContact = await this.openNotValidatedContactDialog();
		}

		if (!allowPrimaryContact) {
			event.source.checked = false;
			return;
		}

		let updatedContact: ContactsTable = Object.assign(
				{},
				this.contactList.find((contact) => contact.id == carrierContactMapId)
			),
			contact = Object.assign({}, updatedContact);
		contact.primaryContact = !isPrimaryContact;
		updatedContact.primaryContact = isPrimaryContact;
		this.fireContactEvent(true, updatedContact, contact);
		const url = this.urlService.updatePrimaryContact(
			isPrimaryContact,
			carrierContactMapId,
			this.carrierId
		);
		this.restService.POST(url, {}).subscribe((result) => {});
	}

	contactFeedback(contact: ContactsTable) {
		const dialogRef = this.dialog.open(CarrierDataFeedbackDialog, {
			width: '900px',
			data: {
				lane: this.lane,
				incorrectDataType: 'contact',
				contact: contact,
			},
		});
	}

	openContactDialog(contact: ContactsTable) {
		const isEditContact = !this.utilService.isNullOrUndefined(contact);
		const dialogRef = this.dialog.open(AddContactFormDialog, {
			width: '500px',
			data: {
				carrierid: this.carrierId,
				isEdit: isEditContact,
				contact: contact,
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (!this.utilService.isNullOrUndefined(result)) {
				if (isEditContact) {
					const indexUpdated = this.contactList.findIndex((contact) => {
						return contact.id == result.id;
					});
					if (this.isCarrierUpdate) {
						this.fireContactEvent(isEditContact, result, this.contactList[indexUpdated]);
					}
					this.contactList[indexUpdated] = Object.assign({}, result);
				} else {
					this.fireContactEvent(isEditContact, result);
					this.updateContactsListView(result);
				}
				this.updateContactsList.emit(this.contactList);
			}
		});
	}

	fireContactEvent(
		isEditContact: boolean,
		updatedContact: ContactsTable,
		contact: ContactsTable = null
	) {
		if (this.isCarrierLoggedIn) {
			let changedProperties = {},
				eventName = SegmentEventsConstants.carrier_added_contact;
			if (isEditContact) {
				eventName = SegmentEventsConstants.carrier_updated_contact;
				Object.keys(updatedContact)
					.filter((k) => updatedContact[k] !== contact[k])
					.filter((key) => key != 'updatedAt')
					.filter((key) => {
						changedProperties[key] = updatedContact[key];
					});
				changedProperties = JSON.stringify(changedProperties)
					.replace(/\"/g, '')
					.replace(/\{/g, '')
					.replace(/\}/g, '');
			} else {
				changedProperties = `New contact added`;
			}
			const payload: any = {
				contact: updatedContact,
				carrier: this.cargochief.CARRIER,
				url: `${environment.clientUrl}admin/carrier_profile/${this.carrierId}`,
				updated: changedProperties,
			};
			this.segmentWrapper.fireSegmentEvent(eventName, payload);
		}
	}

	async reportDataAction(dataType: string) {
		await this.carrierFeedbackService.reportDataAction(dataType, this.searchTruckForm, this.lane);
	}

	async setAccountCarrierContact(element) {
		const request = this.restService.POST(this.urlService.setCarrierAccountContact(), element);

		request.subscribe(
			(response) => {
				this.utilService.showSnackbar(
					'Successfully set email recipient.',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				this.refreshContactsList();
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to set email recipient.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	// CN-8616: only when the V2 bid list passed load + bid context — clicking a
	// contact's phone/email also opens the shared "Log a call" modal (alongside the
	// tel:/mailto: the anchor already fires) and, on save, marks the bid Contacted.
	// A no-op everywhere else (carrier profile, projects, offers) so those keep plain
	// tel:/mailto: links.
	onContactChannelClick(): void {
		if (!this.logCallLoadId || !this.logCallBidId || !this.carrierId) {
			return;
		}

		this.logCallService
			.openLogCall({
				carrierId: this.carrierId,
				loadId: this.logCallLoadId,
				bidId: this.logCallBidId,
			})
			.pipe(takeUntil(this.isDestroyed$))
			// CN-8646: openLogCall now returns { bidId, note }; the modal path only needs the bidId.
			.subscribe((result) => this.contactLogged.emit(result.bidId));
	}

	async refreshContactsList() {
		const contactsUrl = this.urlService.contactsUrl(this.carrierId);
		try {
			const contactsListResponse = await this.restService.GET(contactsUrl).toPromise();
			if (contactsListResponse?.data) {
				this.contactList = contactsListResponse.data;
				this.buildDatatableView();
				this.updateContactsList.emit(this.contactList);
			}
		} catch (error) {
			console.error('Error refreshing contacts list:', error);
		}
	}

	showContactSearchDialog() {
		const dialogRef = this.dialog.open(SearchContactsDialogComponent, {
			width: '600px',
			data: {
				carrierId: this.carrierId,
				contactList: this.contactList,
			},
		});

		dialogRef.afterClosed().subscribe((contact) => {
			if (contact) {
				this.mapContactToProject(
					contact.emailAddress,
					this.projectCarrierMap.id,
					contact.contactId,
					contact.id
				);
			}
		});
	}

	async mapContactToProjectColumn(
		contact: ContactsTable,
		removeContact: boolean = false,
		primary: boolean = false,
		successMessage: string = 'Carrier contacts for this project updated'
	) {
		this.mapContactToProject(
			contact.emailAddress,
			this.projectCarrierMap.id,
			contact.contactId,
			contact.id,
			removeContact,
			primary,
			successMessage
		);
	}

	// Set offer (TO) contact: POST primary=true; the previous offer stays mapped as a CC
	async setOfferContact(contact: ContactsTable) {
		if (contact.isOfferContact) {
			return;
		}
		const confirmed = await this.confirmContactAction(
			`You are setting ${contact.emailAddress} as the offer (TO) contact for ${this.carrier?.businessName || ''} on all loads matching this project. Load offers and the invite "To" line go to this contact; other selected contacts are CC'd.`,
			'Confirm'
		);
		if (!confirmed) {
			this.cdRef.detectChanges();
			return;
		}
		this.mapContactToProjectColumn(contact, false, true, 'Offer (TO) contact updated.');
	}

	// CC toggle: additive add when checked, remove when unchecked
	toggleCcContact(event, contact: ContactsTable) {
		const addCc = event.checked;
		this.mapContactToProjectColumn(
			contact,
			!addCc,
			false,
			addCc ? 'Contact added as CC.' : 'Contact removed from CC.'
		);
	}

	private async confirmContactAction(body: string, confirmButtonText: string): Promise<boolean> {
		return this.dialog
			.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					title: '',
					body,
					cancelButtonText: 'Cancel',
					ConfirmButtonText: confirmButtonText,
				},
				disableClose: true,
			})
			.afterClosed()
			.toPromise()
			.then((response) => response === true);
	}

	async mapContactToProject(
		contactEmailAddress: string,
		projectCarrierMapId: string,
		contactId: string,
		carrierContactMapId: string,
		removeContact: boolean = false,
		primary: boolean = false,
		successMessage: string = 'Carrier contacts for this project updated'
	) {
		const updateRequest: any = {
			accountId: this.cargochief.ACCOUNT_ID,
			projectCarrierMapId,
			contactId,
			carrierContactMapId,
		};
		// primary only set for the offer (TO) contact; absent = additive CC
		if (primary) {
			updateRequest.primary = true;
		}

		const restRequest = removeContact
			? this.restService.DELETE(
					this.urlService.updateProjectCarrierMapContact(this.projectCarrierMap.id),
					updateRequest
				)
			: this.restService.POST(
					this.urlService.updateProjectCarrierMapContact(this.projectCarrierMap.id),
					updateRequest
				);

		restRequest.subscribe(
			(response) => {
				// Reflect server state so adding a CC no longer unmaps the other contacts
				this.projectCarrierMap = response;
				this.onProjectCarrierMapContact.emit(response);
				this.buildDatatableView();
				this.updateContactsList.emit(this.contactList);
				this.selectedContactRow = null;
				if (IS_NOT_EMPTY(successMessage)) {
					this.utilService.showSnackbar(successMessage, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				}
				this.cdRef.detectChanges();
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to update project contacts.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				// revert the optimistic toggle state
				this.cdRef.detectChanges();
			}
		);
	}

	// Remove the offer (TO) contact; server promotes the next primary or falls back to carrier default
	async removeOfferContact(contact: ContactsTable) {
		const confirmed = await this.confirmContactAction(
			`Remove ${contact.emailAddress} as the offer (TO) contact for ${this.carrier?.businessName || ''}? Load offers will fall back to this carrier's default contact.`,
			'Remove'
		);
		if (confirmed) {
			this.mapContactToProjectColumn(contact, true, false, 'Offer contact removed.');
		}
	}

	async updateContactsListView(newContact: ContactsTable) {
		const contactsUrl = this.urlService.contactsUrl(this.carrierId);
		const contactsListResponse = await this.restService.GET(contactsUrl).toPromise();
		if (contactsListResponse?.data) {
			this.contactList = contactsListResponse.data;
			this.updateContactsList.emit(this.contactList);
			this.cdRef.detectChanges();
			const contact = this.contactList.find((c) => c.id === newContact.contactId);
			if (contact && this.projectCarrierMap) {
				this.mapNewContactAddedToProject(contact);
			}
		}
	}

	async mapNewContactAddedToProject(contact: ContactsTable) {
		// additive add of a new contact (add-contact dialog already snackbars)
		this.mapContactToProjectColumn(contact, false, false, '');
	}

	onContactRowSelection(contact: ContactsTable): void {
		this.selectedContactRow = this.selectedContactRow?.id === contact.id ? null : contact;
	}

	async openNotValidatedContactDialog() {
		return await this.dialog
			.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					title: 'Contact is not validated',
					body: `You are setting a contact that is not validated as the primary contact.
						This will trigger an automated email to the carrier to confirm the contact.
						Please call the carrier after and ask them to look for the email. `,
					cancelButtonText: 'Cancel',
					ConfirmButtonText: 'Confirm',
				},
				disableClose: true,
			})
			.afterClosed()
			.toPromise()
			.then((response) => {
				return response === true;
			});
	}

	loadBidContactRoles() {
		if (!this.showBidContactsInfo || !IS_NOT_EMPTY(this.logCallBidId)) {
			this.bidOfferContactId = null;
			this.bidCcContactIds = [];
			return;
		}
		this.restService
			.GET(this.urlService.bidContactsUrl(this.logCallBidId))
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((response) => {
				const bidContacts = Array.isArray(response) ? response : response?.data || [];
				this.bidOfferContactId = bidContacts.length ? bidContacts[0].id : null;
				this.bidCcContactIds = bidContacts.slice(1).map((contact) => contact.id);
				this.buildDatatableView();
			});
	}

	applyBidContactRoles() {
		if (!this.showBidContactsInfo || this.utilService.isNullOrUndefined(this.contactList)) {
			return;
		}
		this.contactList.forEach((contact) => {
			contact.bidToContact = contact.id === this.bidOfferContactId;
			contact.ccContact = this.bidCcContactIds.includes(contact.id);
		});
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
